import { useEffect, useState, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { Send } from "lucide-react";
import { Header } from "@/components/Header";
import { apiFetch } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const Avatar = ({ person, size = "h-10 w-10" }) =>
  person?.picture ? (
    <img src={person.picture} alt={person.name} className={`${size} shrink-0 rounded-full object-cover`} />
  ) : (
    <span className={`${size} grid shrink-0 place-items-center rounded-full bg-neutral-900 text-xs font-bold text-white`}>
      {(person?.name || "?")[0].toUpperCase()}
    </span>
  );

export default function Messages() {
  const { user } = useAuth();
  const { search } = useLocation();
  const [threads, setThreads] = useState([]);
  const [members, setMembers] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [thread, setThread] = useState(null);
  const [body, setBody] = useState("");

  const loadSidebar = useCallback(() => {
    apiFetch("/dm/threads").then((r) => setThreads(r.threads)).catch(() => {});
    apiFetch("/members").then((r) => setMembers(r.members)).catch(() => {});
  }, []);

  const loadThread = useCallback(async (id) => {
    setActiveId(id);
    setThread(await apiFetch(`/dm/${id}`).catch(() => null));
    loadSidebar();
  }, [loadSidebar]);

  useEffect(() => {
    loadSidebar();
  }, [loadSidebar]);

  useEffect(() => {
    const to = new URLSearchParams(search).get("to");
    if (to) loadThread(to);
  }, [search, loadThread]);

  const send = async (e) => {
    e.preventDefault();
    try {
      await apiFetch(`/dm/${activeId}`, { method: "POST", body: JSON.stringify({ body }) });
      setBody("");
      loadThread(activeId);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const threadIds = threads.map((t) => t.member.user_id);
  const others = members.filter((m) => !threadIds.includes(m.user_id));

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-extrabold tracking-tight text-neutral-900 sm:text-4xl">Messages</h1>
        <p className="mt-2 text-sm text-neutral-500 md:text-lg">Direct, one-to-one with other members.</p>

        <div className="mt-8 grid gap-8 lg:grid-cols-[300px_1fr]">
          <aside>
            {threads.length > 0 && (
              <>
                <p className="mb-2 text-xs font-bold uppercase tracking-wide text-neutral-400">Conversations</p>
                <ul className="mb-6 space-y-1" data-testid="thread-list">
                  {threads.map((t) => (
                    <li key={t.member.user_id}>
                      <button
                        onClick={() => loadThread(t.member.user_id)}
                        data-testid={`thread-${t.member.user_id}`}
                        className={`flex w-full items-center gap-3 rounded-xl p-2.5 text-left transition-colors duration-200 ${
                          activeId === t.member.user_id ? "bg-neutral-100" : "hover:bg-neutral-50"
                        }`}
                      >
                        <Avatar person={t.member} size="h-9 w-9" />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-semibold text-neutral-900">{t.member.name}</span>
                          <span className="block truncate text-xs text-neutral-500">{t.last_message}</span>
                        </span>
                        {t.unread > 0 && (
                          <span className="grid h-5 min-w-5 place-items-center rounded-full bg-[#B8FF2E] px-1.5 text-[10px] font-bold text-[#14210A]">
                            {t.unread}
                          </span>
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              </>
            )}

            <p className="mb-2 text-xs font-bold uppercase tracking-wide text-neutral-400">Members</p>
            <ul className="space-y-1" data-testid="dm-member-list">
              {others.length === 0 && <li className="text-sm text-neutral-500">No other members yet.</li>}
              {others.map((m) => (
                <li key={m.user_id}>
                  <button
                    onClick={() => loadThread(m.user_id)}
                    data-testid={`member-${m.user_id}`}
                    className="flex w-full items-center gap-3 rounded-xl p-2.5 text-left transition-colors duration-200 hover:bg-neutral-50"
                  >
                    <Avatar person={m} size="h-9 w-9" />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-semibold text-neutral-900">{m.name}</span>
                      <span className="block truncate text-xs text-neutral-500">{m.role}</span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </aside>

          <section className="rounded-2xl border border-neutral-200">
            {!thread ? (
              <div className="grid h-80 place-items-center text-sm text-neutral-500" data-testid="dm-empty">
                Pick a member to start a conversation.
              </div>
            ) : (
              <div className="flex h-[540px] flex-col">
                <div className="flex items-center gap-3 border-b border-neutral-200 p-4">
                  <Avatar person={thread.member} size="h-9 w-9" />
                  <p className="text-sm font-bold text-neutral-900" data-testid="dm-header">
                    <Link to={`/members/${thread.member.user_id}`} className="hover:underline">
                      {thread.member.name}
                    </Link>
                  </p>
                </div>

                <div className="flex-1 space-y-3 overflow-auto p-4" data-testid="dm-messages">
                  {thread.messages.length === 0 && (
                    <p className="text-sm text-neutral-500">No messages yet — say hello.</p>
                  )}
                  {thread.messages.map((m) => {
                    const mine = m.from_id === user?.user_id;
                    return (
                      <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                        <p
                          className={`max-w-[75%] whitespace-pre-line rounded-2xl px-4 py-2.5 text-sm ${
                            mine ? "bg-[#B8FF2E] text-[#14210A]" : "bg-neutral-100 text-neutral-800"
                          }`}
                        >
                          {m.body}
                        </p>
                      </div>
                    );
                  })}
                </div>

                <form onSubmit={send} className="flex gap-2 border-t border-neutral-200 p-4">
                  <input
                    required
                    data-testid="dm-input"
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    placeholder={`Message ${thread.member.name}…`}
                    className="flex-1 rounded-full border border-neutral-300 px-4 py-2.5 text-sm outline-none transition-colors duration-200 focus:border-neutral-900"
                  />
                  <button
                    data-testid="dm-send"
                    className="grid h-11 w-11 place-items-center rounded-full bg-[#B8FF2E] text-[#14210A] transition-colors duration-200 hover:bg-[#A2E80B]"
                  >
                    <Send className="h-4 w-4" />
                  </button>
                </form>
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
